/**
 * Display names for deal stages and pipelines that Coffee seeded itself.
 *
 * The backend stamps an `i18nKey` on every stage and pipeline it creates, and
 * CLEARS that key the moment someone renames one. So the key is exactly the
 * signal "this is still Coffee's shipped wording, translate it"; once it is
 * null, `name` is the user's own words and stored user data is never
 * translated.
 *
 * This resolves the LABEL only. `name` stays the identifier — deals are matched
 * to kanban columns by it, column widths are persisted under it, and filters
 * send it on the wire. Never compare or store what this function returns.
 */
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import type { TFunction } from "i18next";

/** The two fields a label needs. Structural, so any stage-ish shape fits. */
export interface TranslatableStage {
  name: string;
  i18nKey?: string | null;
}

/**
 * The label to show for a stage: its translated shipped wording, or the name
 * the user gave it.
 *
 * The `defaultValue` is the sanctioned form here, not a way to dodge adding a
 * key: the key comes from a backend row, so the frontend cannot guarantee it
 * ships one — the backend can seed a new default stage a release before the
 * catalogs carry its translation. Without the default, i18next would echo the
 * key and put "stage.default.discovery" on screen; with it, that workspace sees
 * the English name for one release. (Grid column labels use the same escape
 * hatch for the same reason — see the missing-key handler in `src/test/setup.ts`.)
 */
export function stageLabel(stage: TranslatableStage | null | undefined, t: TFunction): string {
  if (!stage) return "";
  const key = stage.i18nKey;
  if (!key) return stage.name;
  return t(key, { defaultValue: stage.name });
}

/**
 * `stageLabel` bound to the active language, for components.
 *
 * Returns a stable callback so it can be used inside `useMemo` deps without
 * re-running the memo on every render.
 */
export function useStageLabel(): (stage: TranslatableStage | null | undefined) => string {
  const { t } = useTranslation();
  return useCallback((stage: TranslatableStage | null | undefined) => stageLabel(stage, t), [t]);
}

/**
 * The same resolution for a pipeline, which carries `i18nKey` on exactly the
 * same terms (`pipeline.default.sales`, `pipeline.default.onboarding`; cleared
 * on rename).
 *
 * Named separately rather than pointing call sites at `stageLabel` so that
 * `pipelineLabel(pipeline)` does not read like a copy-paste slip. And, as with
 * stages, this resolves the LABEL only: `name` is still the identifier the
 * setup wizard matches on when it looks for "Default Pipeline".
 */
export function pipelineLabel(
  pipeline: TranslatableStage | null | undefined,
  t: TFunction,
): string {
  return stageLabel(pipeline, t);
}

/** `pipelineLabel` bound to the active language, for components. */
export function usePipelineLabel(): (pipeline: TranslatableStage | null | undefined) => string {
  const { t } = useTranslation();
  return useCallback(
    (pipeline: TranslatableStage | null | undefined) => pipelineLabel(pipeline, t),
    [t],
  );
}
